import React from 'react';
import { Shield, Eye, Lock, Users, Database, Phone } from 'lucide-react';

export const PrivacyPolicy: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-md border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img 
                src="/zippy-logo-simple.svg" 
                alt="ZippyHealth Logo"
                className="w-8 h-8"
              />
              <div>
                <h1 className="text-xl font-bold text-slate-800">ZippyHealth.io</h1>
                <p className="text-xs text-slate-500">Privacy Policy</p>
              </div>
            </div>
            <button 
              onClick={() => window.history.back()}
              className="text-slate-600 hover:text-slate-800 font-medium"
            >
              ← Back
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        
        {/* Header */}
        <div className="text-center mb-12">
          <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Shield className="h-8 w-8 text-blue-600" />
          </div>
          <h1 className="text-4xl font-bold text-slate-900 mb-4">Privacy Policy</h1>
          <p className="text-xl text-slate-600 mb-2">
            Your privacy and health data security are our top priorities
          </p>
          <p className="text-sm text-slate-500">
            Last updated: December 2024 • Effective Date: January 1, 2025
          </p>
        </div>

        {/* Key Principles */}
        <div className="bg-gradient-to-r from-blue-50 to-green-50 rounded-2xl p-8 mb-12">
          <h2 className="text-2xl font-bold text-slate-900 mb-6 text-center">Our Privacy Principles</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <Eye className="h-8 w-8 text-blue-600 mx-auto mb-3" />
              <h3 className="font-semibold text-slate-900 mb-2">Transparency</h3>
              <p className="text-sm text-slate-600">You always know what data we collect and why</p>
            </div>
            <div className="text-center">
              <Lock className="h-8 w-8 text-green-600 mx-auto mb-3" />
              <h3 className="font-semibold text-slate-900 mb-2">Security</h3>
              <p className="text-sm text-slate-600">Your data is protected with enterprise-grade encryption</p>
            </div>
            <div className="text-center">
              <Users className="h-8 w-8 text-purple-600 mx-auto mb-3" />
              <h3 className="font-semibold text-slate-900 mb-2">Control</h3>
              <p className="text-sm text-slate-600">You decide what to share and with whom</p>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-2xl shadow-lg p-8 space-y-8">

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">1. Information We Collect</h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-slate-800 mb-3">Health Information</h3>
                <ul className="space-y-2 text-slate-600">
                  <li>• Medical conditions, symptoms, and treatments you track</li>
                  <li>• Medication information and adherence data</li>
                  <li>• Food intake and nutritional information</li>
                  <li>• Vital signs and health metrics from connected devices</li>
                  <li>• Lab results and medical documents you upload</li>
                  <li>• Appointment and healthcare provider information</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-slate-800 mb-3">Account Information</h3>
                <ul className="space-y-2 text-slate-600">
                  <li>• Name, email address, and contact information</li>
                  <li>• Profile information and preferences</li>
                  <li>• Authentication credentials and security settings</li>
                  <li>• Billing information for paid plans</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-slate-800 mb-3">Usage Information</h3>
                <ul className="space-y-2 text-slate-600">
                  <li>• How you interact with our platform features</li>
                  <li>• Device information and technical data</li>
                  <li>• Analytics on feature usage and performance</li>
                  <li>• Error logs and diagnostic information</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">2. How We Use Your Information</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-blue-50 rounded-xl p-6">
                <Database className="h-6 w-6 text-blue-600 mb-3" />
                <h3 className="font-semibold text-slate-900 mb-2">Core Services</h3>
                <ul className="text-sm text-slate-600 space-y-1">
                  <li>• Provide health tracking functionality</li>
                  <li>• Generate insights and analytics</li>
                  <li>• Sync data across your devices</li>
                  <li>• Facilitate provider collaboration</li>
                </ul>
              </div>
              
              <div className="bg-green-50 rounded-xl p-6">
                <Shield className="h-6 w-6 text-green-600 mb-3" />
                <h3 className="font-semibold text-slate-900 mb-2">Safety & Security</h3>
                <ul className="text-sm text-slate-600 space-y-1">
                  <li>• Protect against fraud and abuse</li>
                  <li>• Maintain platform security</li>
                  <li>• Comply with legal obligations</li>
                  <li>• Respond to emergencies</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">3. Information Sharing</h2>
            
            <div className="bg-red-50 rounded-xl p-6 mb-6">
              <h3 className="font-semibold text-red-800 mb-2">We Never Sell Your Health Data</h3>
              <p className="text-red-700 text-sm">
                Your health information is never sold to third parties. We only share data with your explicit consent or as required by law.
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-slate-800 mb-2">With Your Permission</h3>
                <ul className="space-y-1 text-slate-600">
                  <li>• Healthcare providers you authorize</li>
                  <li>• Family members you designate</li>
                  <li>• Research studies you opt into</li>
                  <li>• Third-party apps you connect</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-slate-800 mb-2">Service Providers</h3>
                <p className="text-slate-600 mb-2">We work with trusted partners who help us operate our service:</p>
                <ul className="space-y-1 text-slate-600">
                  <li>• Cloud hosting and data storage</li>
                  <li>• Payment processing</li>
                  <li>• Analytics and performance monitoring</li>
                  <li>• Customer support tools</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">4. Data Security</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold text-slate-800 mb-3">Technical Safeguards</h3>
                <ul className="space-y-2 text-slate-600">
                  <li>• AES-256 encryption at rest and in transit</li>
                  <li>• Multi-factor authentication</li>
                  <li>• Regular security audits and penetration testing</li>
                  <li>• SOC 2 certified infrastructure*</li>
                  <li>• HIPAA-compliant data handling*</li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold text-slate-800 mb-3">Access Controls</h3>
                <ul className="space-y-2 text-slate-600">
                  <li>• Role-based access management</li>
                  <li>• Audit logging of all data access</li>
                  <li>• Employee background checks</li>
                  <li>• Principle of least privilege</li>
                  <li>• Regular access reviews</li>
                </ul>
              </div>
            </div>
            
            <p className="text-sm text-slate-500 mt-4">
              *Certifications pending - we are actively pursuing full compliance
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">5. Your Rights and Choices</h2>
            
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-xl p-6">
                <h3 className="font-semibold text-slate-900 mb-3">You Have the Right To:</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <ul className="space-y-2 text-slate-600">
                    <li>• Access your personal information</li>
                    <li>• Correct inaccurate data</li>
                    <li>• Delete your account and data</li>
                    <li>• Export your data</li>
                  </ul>
                  <ul className="space-y-2 text-slate-600">
                    <li>• Restrict data processing</li>
                    <li>• Withdraw consent</li>
                    <li>• File complaints with authorities</li>
                    <li>• Receive data breach notifications</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">6. Data Retention</h2>
            <div className="space-y-4">
              <p className="text-slate-600">
                We retain your health data for as long as your account is active or as needed to provide services. 
                When you delete your account:
              </p>
              <ul className="space-y-2 text-slate-600 ml-6">
                <li>• Personal data is deleted within 30 days</li>
                <li>• Backup copies are purged within 90 days</li>
                <li>• Anonymized data may be retained for research</li>
                <li>• Legal holds may extend retention periods</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">7. Children's Privacy</h2>
            <p className="text-slate-600">
              Our service is not intended for children under 13. We do not knowingly collect personal information 
              from children under 13. If we learn we have collected such information, we will delete it immediately.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">8. International Transfers</h2>
            <p className="text-slate-600 mb-4">
              Your data may be transferred to and processed in countries other than your own. We ensure adequate 
              protection through:
            </p>
            <ul className="space-y-2 text-slate-600 ml-6">
              <li>• Adequacy decisions by regulatory authorities</li>
              <li>• Standard contractual clauses</li>
              <li>• Certification schemes and codes of conduct</li>
              <li>• Binding corporate rules</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">9. Changes to This Policy</h2>
            <p className="text-slate-600">
              We may update this privacy policy from time to time. We will notify you of any material changes 
              by email and through our platform. Your continued use of our service after such modifications 
              constitutes your acknowledgment and acceptance of the updated policy.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">10. Contact Us</h2>
            <div className="bg-blue-50 rounded-xl p-6">
              <p className="text-slate-600 mb-4">
                If you have questions about this privacy policy or our data practices, please contact us:
              </p>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Phone className="h-4 w-4 text-blue-600" />
                  <span className="text-slate-700">privacy@zippyhealth.io</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Users className="h-4 w-4 text-blue-600" />
                  <span className="text-slate-700">Data Protection Officer</span>
                </div>
                <div className="text-sm text-slate-600 mt-4">
                  <strong>Mailing Address:</strong><br />
                  ZippyHealth.io Privacy Team<br />
                  123 Health Technology Blvd<br />
                  Innovation City, IC 12345<br />
                  United States
                </div>
              </div>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy; 
